import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";
import { Send, Bot } from "lucide-react";

export default function TestPromptDialog({ open, onOpenChange, model }) {
  const [prompt, setPrompt] = useState("Hola, ¿me escuchas?");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  async function send(e) {
    e.preventDefault();
    if (!prompt.trim()) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/ai/models/${encodeURIComponent(model.id)}/prompt`,
        { prompt, timeout_ms: 60000 }
      );
      setResult(data);
      toast.success("Respuesta recibida");
    } catch (err) {
      setResult({ error: apiErrorMessage(err) });
      toast.error(apiErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  if (!model) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.aiModels.testDialog}
        className="bg-zinc-950 border border-zinc-800 text-zinc-100 sm:max-w-[640px] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Bot className="h-4 w-4" />
            Probar {model.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Envía un prompt de prueba. El navegador remoto abrirá{" "}
            <span className="font-mono">{model.url}</span> con el perfil{" "}
            <span className="font-mono text-zinc-300">{model.profile}</span>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={send} className="space-y-4">
          <Textarea
            data-testid={TID.aiModels.testPrompt}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            className="bg-zinc-900 border-zinc-800 text-sm"
            placeholder="Escribe tu prompt…"
          />

          {result && (
            <div
              data-testid={TID.aiModels.testResult}
              className="rounded-md border border-zinc-800 bg-black/40 p-4 text-xs space-y-2"
            >
              {result.error ? (
                <div className="text-red-400 font-mono whitespace-pre-wrap">
                  {result.error}
                </div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center justify-between">
                    <span>Respuesta</span>
                    <span>{result.duration_ms ? `${result.duration_ms} ms` : ""}</span>
                  </div>
                  <div className="text-zinc-200 whitespace-pre-wrap leading-relaxed max-h-[40vh] overflow-y-auto">
                    {result.response || "(vacío)"}
                  </div>
                </>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800"
            >
              Cerrar
            </Button>
            <Button
              type="submit"
              data-testid={TID.aiModels.testSend}
              disabled={running || !prompt.trim()}
              className="bg-white text-black hover:bg-zinc-200"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {running ? "Enviando…" : "Enviar prompt"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
